import { useEffect, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import Col from "react-bootstrap/Col"
import Button from "react-bootstrap/Button"
import style from "../../components/Form.module.scss"
import Warnings from "../../components/Warning"
import { SecurityLockError, SecurityLockService } from "../../services/SecurityLockService"
import { PageEnum } from "../../enums/pageEnum"
import { navigateToPage } from "../../services/navigateService"

type PageState = "loading" | "confirm" | "success" | "error"

const ERROR_MESSAGES: Record<string, string> = {
    INVALID_LOCK_TOKEN: "Este link de segurança é inválido.",
    LOCK_TOKEN_EXPIRED_OR_USED: "Este link já foi utilizado ou expirou.",
    SECURITY_LOCK_FAILED: "Não foi possível bloquear a conta.",
}

function resolveErrorMessage(error: SecurityLockError | null, fallback: string): string {
    if (!error) return fallback
    return ERROR_MESSAGES[error.code] ?? fallback
}

export default function SecurityLockAccount() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const routeParams = useParams()
    const token = searchParams.get("token") || ""

    const [pageState, setPageState] = useState<PageState>("loading")
    const [maskedEmail, setMaskedEmail] = useState("")
    const [errorMessage, setErrorMessage] = useState("")
    const [isConfirming, setIsConfirming] = useState(false)

    useEffect(() => {
        if (!token) {
            setErrorMessage(ERROR_MESSAGES.INVALID_LOCK_TOKEN)
            setPageState("error")
            return
        }

        let cancelled = false

        SecurityLockService.previewLockAccount(token)
            .then((data) => {
                if (cancelled) return
                setMaskedEmail(data.maskedEmail)
                setPageState("confirm")
            })
            .catch((err: SecurityLockError) => {
                if (cancelled) return
                setErrorMessage(resolveErrorMessage(err, ERROR_MESSAGES.INVALID_LOCK_TOKEN))
                setPageState("error")
            })

        return () => {
            cancelled = true
        }
    }, [token])

    async function handleConfirm() {
        if (!token || isConfirming) return

        setIsConfirming(true)
        setErrorMessage("")

        try {
            await SecurityLockService.confirmLockAccount(token)
            setPageState("success")
        } catch (err) {
            const lockError = err as SecurityLockError
            setErrorMessage(resolveErrorMessage(lockError, ERROR_MESSAGES.SECURITY_LOCK_FAILED))
            setPageState("error")
            setIsConfirming(false)
        }
    }

    function handleItsMe() {
        navigateToPage(navigate, routeParams, PageEnum.LOGIN)
    }

    if (pageState === "loading") {
        return (
            <Col className={style.colForm} style={{ margin: '0px 16px' }}>
                <p style={{ textAlign: "center" }}>Carregando...</p>
            </Col>
        )
    }

    if (pageState === "success") {
        return (
            <Col className={style.colForm} style={{ margin: '0px 16px' }}>
                <h2 style={{ color: "#2674B5", marginBottom: "16px", textAlign: 'center' }}>Conta bloqueada</h2>
                <Warnings
                    type="success"
                    warning="Sua conta foi bloqueada com sucesso."
                />
                <p style={{ lineHeight: 1.5, marginTop: "16px" }}>
                    Se você não reconhece a atividade recente, entre em contato com o <strong>suporte</strong> da Mobilemed.
                </p>
            </Col>
        )
    }

    if (pageState === "error") {
        return (
            <Col className={style.colForm} style={{ margin: '0px 16px' }}>
                <h2 style={{ color: "#2674B5", marginBottom: "16px", textAlign: 'center' }}>Não foi possível continuar</h2>
                <Warnings type="error" warning={errorMessage} />
                <p style={{ color: "#666", fontSize: "13px", marginTop: "16px" }}>
                    O link pode ter expirado, já ter sido usado ou ser inválido.
                </p>
            </Col>
        )
    }

    return (
        <Col className={style.colForm} style={{ margin: '0px 16px' }}>
            <h2 style={{ color: "#2674B5", marginBottom: "16px", textAlign: 'center' }}>Bloquear conta</h2>
            <p style={{ lineHeight: 1.5, textAlign: 'center' }}>
                Você solicitou o bloqueio da conta:<br /> <strong>{maskedEmail}</strong><br /> por suspeita de acesso não autorizado.
            </p>
            <Warnings
                type="warning"
                warning="Esta ação desativa o login imediatamente. Somente o <strong>suporte</strong> poderá reativar a conta."
            />
            <div style={{ marginTop: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
                {isConfirming ? (
                    <Button
                        variant="danger"
                        disabled
                        style={{ width: "100%" }}
                    >
                        Bloqueando...
                    </Button>
                ) : (
                    <>
                        <Button
                            variant="danger"
                            onClick={handleConfirm}
                            style={{ width: "100%" }}
                        >
                            Bloquear conta
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={handleItsMe}
                            style={{
                                width: "100%",
                                color: "#2674B5",
                                borderColor: "#2674B5",
                                backgroundColor: "transparent",
                            }}
                        >
                            Sou eu mesmo
                        </Button>
                    </>
                )}
            </div>
        </Col>
    )
}
